import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { TWBadge, TWBadgeColor } from '../ui/TWBadge';
import { Announcement, markAsRead } from '../../services/content/announcementService';
import { ImageLightboxTW } from '../blog/ImageLightboxTW';
import { getApiBaseUrl } from '../../services/api/apiClient';

// =====================================================
// HILFSFUNKTIONEN
// =====================================================

const audienceLabel: Record<string, string> = {
  all: 'Alle',
  admin: 'Admin',
  beta: 'Beta',
  subscriber: 'Abonnenten',
};

const audienceColor: Record<string, TWBadgeColor> = {
  all: 'gray',
  admin: 'brand',
  beta: 'blue',
  subscriber: 'green',
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

// =====================================================
// KOMPONENTE
// =====================================================

interface AnnouncementModalProps {
  announcement: Announcement | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkedRead?: () => void;
}

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  announcement,
  isOpen,
  onClose,
  onMarkedRead,
}) => {
  const [lightboxSrc, setLightboxSrc] = useState<{ src: string; alt: string } | null>(null);

  // Als gelesen markieren beim Öffnen
  useEffect(() => {
    if (isOpen && announcement && !announcement.is_read) {
      markAsRead(announcement.id).then(() => {
        onMarkedRead?.();
      }).catch(() => {
        // Fehler beim Markieren ignorieren
      });
    }
  }, [isOpen, announcement, onMarkedRead]);

  // Lightbox zurücksetzen wenn Modal geschlossen wird
  useEffect(() => {
    if (!isOpen) setLightboxSrc(null);
  }, [isOpen]);

  const sanitizedHtml = useMemo(() => {
    if (!announcement?.content_html) return '';
    const fixedHtml = announcement.content_html.replace(/http:\/\/localhost:5555/g, getApiBaseUrl());
    return DOMPurify.sanitize(fixedHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'img', 'figure', 'figcaption', 'blockquote', 'code', 'pre',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span', 'hr',
      ],
      ALLOWED_ATTR: [
        'href', 'title', 'target', 'rel',
        'src', 'alt', 'class', 'id', 'style',
        'width', 'height',
        'data-announcement-image', 'data-width', 'data-alignment',
      ],
    });
  }, [announcement?.content_html]);

  // Klick-Handler für Bilder im Content
  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      setLightboxSrc({ src: img.src, alt: img.alt || '' });
    }
  }, []);

  if (!announcement) return null;

  return (
    <>
      <Dialog open={isOpen} onClose={() => { if (!lightboxSrc) onClose(); }} className="relative z-50">
        <DialogBackdrop
          transition
          className={clsx(
            'fixed inset-0 bg-black/50 backdrop-blur-sm',
            'transition-opacity duration-200 ease-out',
            'data-[closed]:opacity-0'
          )}
        />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            transition
            className={clsx(
              'w-full max-w-3xl flex flex-col',
              'max-h-[90vh]',
              'bg-white dark:bg-gray-900',
              'rounded-xl shadow-2xl',
              'border border-gray-200 dark:border-white/10',
              'transition-all duration-200 ease-out',
              'data-[closed]:opacity-0 data-[closed]:scale-95',
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 shrink-0">
              <DialogTitle className="text-base font-semibold text-gray-900 dark:text-white pr-4">
                {announcement.title}
              </DialogTitle>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-white transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5 scrollbar-styled"
              onWheel={(e) => e.stopPropagation()}
            >
              {/* Meta-Informationen */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(announcement.published_at || announcement.created_at)}
                </span>
                <TWBadge
                  color={audienceColor[announcement.target_audience] || 'gray'}
                  size="sm"
                >
                  {audienceLabel[announcement.target_audience] || announcement.target_audience}
                </TWBadge>
                {announcement.author_name && (
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    von {announcement.author_name}
                  </span>
                )}
              </div>

              {/* Cover-Bild */}
              {announcement.cover_image_url && (
                <img
                  src={announcement.cover_image_url}
                  alt={announcement.title}
                  className="w-full rounded-lg mb-4 max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setLightboxSrc({ src: announcement.cover_image_url!, alt: announcement.title })}
                />
              )}

              {/* HTML-Content */}
              <div
                className="prose prose-sm dark:prose-invert max-w-none leading-[1.5]
                  prose-headings:text-gray-900 dark:prose-headings:text-white
                  prose-p:text-gray-700 dark:prose-p:text-gray-300
                  prose-a:text-brand hover:prose-a:text-brand-hover
                  prose-strong:text-gray-900 dark:prose-strong:text-white
                  prose-img:rounded-lg prose-img:cursor-pointer prose-img:hover:opacity-90 prose-img:transition-opacity"
                dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                onClick={handleContentClick}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
              >
                Schließen
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Zentrale Lightbox mit Pinch-Zoom */}
      <ImageLightboxTW
        isOpen={!!lightboxSrc}
        imageUrl={lightboxSrc?.src || ''}
        alt={lightboxSrc?.alt}
        onClose={() => setLightboxSrc(null)}
      />
    </>
  );
};
